import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Drawer, FormControlLabel, Grid, IconButton, LinearProgress, Link, MenuItem,
  Pagination, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PaidIcon from '@mui/icons-material/Paid';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import MemberTimelinePanel from './MemberTimelinePanel';

const API_BASE_URL = config.API_BASE_URL;

const STATUS_OPTIONS = ['ACTIVE', 'SCHEDULED', 'PENDING', 'SUSPENDED', 'CANCELLED', 'EXPIRED'];

const DEFAULT_FILTERS = {
  statuses: [],
  planId: '',
  teamNames: [],
  search: '',
  withBooksOnly: false,
  nextBillingFrom: '',
  nextBillingTo: '',
  page: 0,
  size: 25,
  sortBy: 'nextBillingDate',
  sortDirection: 'DESC',
};

const statusColor = (status) => ({
  ACTIVE: 'success',
  // Blue, not amber: a subscription waiting for its start date is healthy, not stuck. Amber is
  // reserved for PENDING, which means a payment is in flight right now.
  SCHEDULED: 'info',
  PENDING: 'warning',
  SUSPENDED: 'error',
  CANCELLED: 'default',
  EXPIRED: 'default',
}[status] || 'default');

// How each reconciliation verdict reads. NOT_CHARGED and CANNOT_TELL are deliberately different
// colours: "Square has not billed this member" and "we could not find out" must never look alike.
const VERDICTS = {
  AGREED: { color: 'success', label: 'Agrees with Square' },
  REPAIRABLE: { color: 'warning', label: 'Can be repaired' },
  CHARGED_PAYMENT_UNRECOVERABLE: { color: 'error', label: 'Charged — needs manual fix' },
  NOT_CHARGED: { color: 'info', label: 'Not charged by Square' },
  DIVERGED: { color: 'error', label: 'Diverged' },
  CANNOT_TELL: { color: 'default', label: 'Could not tell' },
};

const paymentColor = (state) => ({
  PAID: 'success',
  FAILED: 'error',
  SCHEDULED: 'info',
  AWAITING_FIRST_PAYMENT: 'warning',
  NO_PAYMENT: 'default',
}[state] || 'default');

const bookColor = (state) => ({
  DELIVERED: 'success',
  SHIPPED: 'info',
  PAID: 'warning',
  PROCESSING: 'warning',
  AWAITING_SHIPMENT: 'warning',
  DEFERRED_OUT_OF_STOCK: 'error',
  PAYMENT_FAILED: 'error',
  CANCELLED: 'default',
  REFUNDED: 'default',
  DIGITAL_ONLY: 'default',
}[state] || 'default');

const prettify = (value) => (value ? String(value).replace(/_/g, ' ') : '—');

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const formatMoney = (value) => (value === null || value === undefined
  ? '—'
  : `$${Number(value).toFixed(2)}`);

const KpiCard = ({ icon: Icon, label, value, color = 'primary.main', helper }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
      <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}><Icon fontSize="small" /></Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
        {helper && <Typography variant="caption" color="text.secondary" display="block">{helper}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

const DetailRow = ({ label, children }) => (
  <>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="caption" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{children}</Typography>
  </>
);

/**
 * DV-252: subscriber management — who is subscribed, what state their subscription
 * and payment are in, and where this cycle's physical book is.
 */
const SubscribersPanel = ({ plans = [], teams = [] }) => {
  const { getAccessToken } = useAuth();

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const apiHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  const callApi = useCallback(async (path, { method = 'GET', body } = {}) => {
    const headers = await apiHeaders();
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method, headers, body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      // The server explains itself — why a reconciliation could not be applied, which
      // subscription is missing. Reporting only the status code throws that away.
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || body.error || `Request failed (${res.status})`);
    }
    const data = await res.json();
    return data.data !== undefined ? data.data : data;
  }, [apiHeaders]);

  // Only send filters the API understands; blank strings mean "no filter".
  const requestBody = useMemo(() => ({
    statuses: filters.statuses.length ? filters.statuses : null,
    planId: filters.planId || null,
    teamNames: filters.teamNames.length ? filters.teamNames : null,
    search: filters.search.trim() || null,
    withBooksOnly: filters.withBooksOnly || null,
    nextBillingFrom: filters.nextBillingFrom ? `${filters.nextBillingFrom}T00:00:00` : null,
    nextBillingTo: filters.nextBillingTo ? `${filters.nextBillingTo}T23:59:59` : null,
    page: filters.page,
    size: filters.size,
    sortBy: filters.sortBy,
    sortDirection: filters.sortDirection,
  }), [filters]);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await callApi('/api/v1/content/admin/subscriptions/search', {
        method: 'POST', body: requestBody,
      });
      setRows(page?.content || []);
      setTotalPages(page?.totalPages || 0);
      setTotalElements(page?.totalElements || 0);
    } catch (e) {
      setError(e.message || 'Failed to load subscribers');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [callApi, requestBody]);

  // ─── Reconcile against Square ───
  // Two steps on purpose: looking writes nothing, applying activates the subscription for real.
  const [reconciling, setReconciling] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  // MemberTimelinePanel has no imperative refresh, so a changing key remounts it after a repair.
  const [timelineKey, setTimelineKey] = useState(0);

  // The same filters the list uses, so the cards count the rows underneath them. Sent without
  // paging: a card counts everything that matches, not what fits on this page.
  const fetchSummary = useCallback(async () => {
    try {
      const { page, size, sortBy, sortDirection, ...summaryFilters } = requestBody;
      setSummary(await callApi('/api/v1/content/admin/subscriptions/summary', {
        method: 'POST', body: summaryFilters,
      }));
    } catch (e) {
      // The table is the primary view; a failed KPI call shouldn't blank the page.
      console.error('Failed to load subscription summary:', e);
    }
  }, [callApi, requestBody]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value, page: 0 }));
  const resetFilters = () => setFilters({ ...DEFAULT_FILTERS });

  const openDetail = async (subscriptionId) => {
    setDrawerOpen(true);
    setDetailLoading(true);
    setDetail(null);
    setReconciliation(null);
    try {
      setDetail(await callApi(`/api/v1/content/admin/subscriptions/${subscriptionId}`));
    } catch (e) {
      setError(e.message || 'Failed to load subscriber detail');
      setDrawerOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const inspectAgainstSquare = async (subscriptionId) => {
    setReconciling(true);
    setReconciliation(null);
    try {
      setReconciliation(await callApi(
        `/api/v1/content/admin/subscriptions/${subscriptionId}/reconcile`));
    } catch (e) {
      setError(e.message || 'Could not check this subscription against Square');
    } finally {
      setReconciling(false);
    }
  };

  const applyReconciliation = async (subscriptionId) => {
    setApplying(true);
    try {
      const after = await callApi(
        `/api/v1/content/admin/subscriptions/${subscriptionId}/reconcile`, { method: 'POST' });
      setReconciliation(after);
      // The row, the drawer and the member's timeline all changed.
      setDetail(await callApi(`/api/v1/content/admin/subscriptions/${subscriptionId}`));
      setTimelineKey((k) => k + 1);
      fetchSubscribers();
      fetchSummary();
    } catch (e) {
      setError(e.message || 'Could not apply the fix');
    } finally {
      setApplying(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const headers = await apiHeaders();
      const res = await fetch(`${API_BASE_URL}/api/v1/content/admin/subscriptions/export`, {
        method: 'POST', headers, body: JSON.stringify(requestBody),
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const teamNames = useMemo(
    () => teams.map((t) => (typeof t === 'string' ? t : t.name)).filter(Boolean),
    [teams],
  );

  const sub = detail?.subscriber;

  return (
    <Box>
      {/* ----------------------------- KPI header ----------------------------- */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <KpiCard icon={PeopleIcon} label="Active" value={summary?.activeSubscribers ?? '—'} color="success.main" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <KpiCard icon={HourglassEmptyIcon} label="Pending" value={summary?.pendingSubscribers ?? '—'} color="warning.main" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <KpiCard icon={PauseCircleIcon} label="Suspended" value={summary?.suspendedSubscribers ?? '—'} color="error.main" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          {/* Deferred sign-ups waiting on their first charge. Without this the counts did not add
              up to the list beneath them, which read as a discrepancy rather than a gap. */}
          <KpiCard icon={EventRepeatIcon} label="Scheduled" value={summary?.scheduledSubscribers ?? '—'} color="info.main" helper="awaiting first charge" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <KpiCard icon={CloseIcon} label="Cancelled" value={summary?.cancelledSubscribers ?? '—'} color="grey.600" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <KpiCard icon={PaidIcon} label="Recurring revenue" value={formatMoney(summary?.recurringRevenue)} color="primary.main" helper="active auto-renew" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <KpiCard icon={EventRepeatIcon} label="Renewals (7d)" value={summary?.renewalsNext7Days ?? '—'} color="info.main" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <KpiCard icon={LocalShippingIcon} label="Awaiting shipment" value={summary?.shipmentsAwaiting ?? '—'} color="warning.dark" />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <KpiCard icon={Inventory2Icon} label="Books deferred" value={summary?.booksDeferred ?? '—'} color="error.dark" helper="out of stock" />
        </Grid>
      </Grid>

      {/* ------------------------------ filters ------------------------------ */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth size="small" label="Search IBO / name / email"
              value={filters.search}
              onChange={(e) => handleFilter('search', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Autocomplete
              multiple size="small" options={STATUS_OPTIONS} value={filters.statuses}
              onChange={(e, v) => handleFilter('statuses', v)}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              select fullWidth size="small" label="Plan"
              value={filters.planId}
              onChange={(e) => handleFilter('planId', e.target.value)}
            >
              <MenuItem value="">All plans</MenuItem>
              {plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              multiple size="small" options={teamNames} value={filters.teamNames}
              onChange={(e, v) => handleFilter('teamNames', v)}
              renderInput={(params) => <TextField {...params} label="Teams" />}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth size="small" type="date" label="Next billing from" InputLabelProps={{ shrink: true }}
              value={filters.nextBillingFrom}
              onChange={(e) => handleFilter('nextBillingFrom', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth size="small" type="date" label="Next billing to" InputLabelProps={{ shrink: true }}
              value={filters.nextBillingTo}
              onChange={(e) => handleFilter('nextBillingTo', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={filters.withBooksOnly}
                  onChange={(e) => handleFilter('withBooksOnly', e.target.checked)}
                />
              )}
              label="Book subscribers only"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={resetFilters} sx={{ textTransform: 'none' }}>Reset</Button>
              <Button
                size="small" variant="outlined" startIcon={<RefreshIcon />}
                onClick={() => { fetchSubscribers(); fetchSummary(); }}
                sx={{ textTransform: 'none' }}
              >
                Refresh
              </Button>
              <Button
                size="small" variant="contained"
                startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                disabled={exporting || !rows.length}
                onClick={handleExport}
                sx={{ textTransform: 'none' }}
              >
                Export CSV
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ------------------------------- table ------------------------------- */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {totalElements} subscriber{totalElements === 1 ? '' : 's'}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Subscriber</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Books</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Receipt</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Next billing</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No subscribers match these filters.
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow
                  key={row.subscriptionId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDetail(row.subscriptionId)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.iboName || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.iboNumber}</Typography>
                  </TableCell>
                  <TableCell>{row.teamName || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.planName || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.planType || ''}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={prettify(row.status)} color={statusColor(row.status)} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={prettify(row.paymentState)} color={paymentColor(row.paymentState)} variant="outlined" />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatDate(row.lastPaymentDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={prettify(row.bookStatus)} color={bookColor(row.bookStatus)} variant="outlined" />
                    {row.bookOrderNumber && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.bookOrderNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {row.lastReceiptUrl ? (
                      <Tooltip title={row.lastReceiptNumber || 'View Square receipt'}>
                        <IconButton size="small" component="a" href={row.lastReceiptUrl} target="_blank" rel="noopener noreferrer">
                          <ReceiptLongIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={row.lastPaymentId ? `Square payment ${row.lastPaymentId}` : 'No payment yet'}>
                        <Typography variant="caption" color="text.secondary">
                          {row.lastPaymentId ? `${row.lastPaymentId.slice(0, 10)}…` : '—'}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(row.nextBillingDate)}</TableCell>
                  <TableCell align="right">{formatMoney(row.totalPrice)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={filters.page + 1}
              onChange={(e, value) => setFilters((prev) => ({ ...prev, page: value - 1 }))}
              size="small"
            />
          </Box>
        )}
      </Paper>

      {/* --------------------------- detail drawer --------------------------- */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', md: 560 } } }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
              {sub?.iboName || 'Subscriber'}
            </Typography>
            <Typography variant="caption" color="text.secondary">{sub?.iboNumber}</Typography>
          </Box>
          {sub?.subscriptionId && (
            <Tooltip title="Ask Square what it holds for this subscription. Checks only — changes nothing.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={reconciling || applying}
                  startIcon={reconciling ? <CircularProgress size={14} /> : <SyncIcon />}
                  onClick={() => inspectAgainstSquare(sub.subscriptionId)}
                  sx={{ textTransform: 'none', mr: 1, whiteSpace: 'nowrap' }}
                >
                  {reconciling ? 'Checking…' : 'Check with Square'}
                </Button>
              </span>
            </Tooltip>
          )}
          <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
        </Box>

        {detailLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : detail && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Subscription</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 1fr', columnGap: 2, rowGap: 1 }}>
                <DetailRow label="Status">
                  <Chip size="small" label={prettify(sub?.status)} color={statusColor(sub?.status)} variant="outlined" />
                </DetailRow>
                <DetailRow label="Plan">{sub?.planName || '—'} ({sub?.planType || '—'})</DetailRow>
                <DetailRow label="Auto renew">{sub?.autoRenew ? 'Yes' : 'No'}</DetailRow>
                <DetailRow label="Started">{formatDate(sub?.startDate)}</DetailRow>
                <DetailRow label="Period ends">{formatDate(sub?.endDate)}</DetailRow>
                <DetailRow label="Next billing">{formatDate(sub?.nextBillingDate)}</DetailRow>
                {sub?.cancelledAt && <DetailRow label="Cancelled">{formatDate(sub.cancelledAt)}</DetailRow>}
                <DetailRow label="Iteration">{sub?.currentIterationNumber ?? '—'}</DetailRow>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Contact</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 1fr', columnGap: 2, rowGap: 1 }}>
                <DetailRow label="Email">{sub?.email || '—'}</DetailRow>
                <DetailRow label="Phone">{sub?.phone || '—'}</DetailRow>
                <DetailRow label="Team">{sub?.teamName || '—'}</DetailRow>
                <DetailRow label="Upline platinum">
                  {sub?.uplinePlatinumIbo ? `${sub.uplinePlatinumIbo} — ${sub.uplinePlatinumIboName || '—'}` : '—'}
                </DetailRow>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>This cycle</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 1fr', columnGap: 2, rowGap: 1 }}>
                <DetailRow label="Content">{formatMoney(sub?.contentPrice)}</DetailRow>
                <DetailRow label="Books">{formatMoney(sub?.bookSubtotal)} ({sub?.bookBundleSets ?? 0} set(s))</DetailRow>
                <DetailRow label="Shipping">{formatMoney(sub?.shippingCost)}</DetailRow>
                <DetailRow label="Tax">{formatMoney(sub?.taxAmount)}</DetailRow>
                <DetailRow label="Total">{formatMoney(sub?.totalPrice)}</DetailRow>
                <DetailRow label="Payment">
                  <Chip size="small" label={prettify(sub?.paymentState)} color={paymentColor(sub?.paymentState)} variant="outlined" />
                </DetailRow>
                <DetailRow label="Square payment">{sub?.lastPaymentId || '—'}</DetailRow>
                <DetailRow label="Receipt">
                  {sub?.lastReceiptUrl
                    ? <Link href={sub.lastReceiptUrl} target="_blank" rel="noopener noreferrer">{sub.lastReceiptNumber || 'View receipt'}</Link>
                    : 'Not captured for this payment'}
                </DetailRow>
              </Box>
            </Paper>

            {reconciliation && (
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
                    Square check
                  </Typography>
                  <Chip
                    size="small"
                    color={(VERDICTS[reconciliation.verdict] || {}).color || 'default'}
                    label={(VERDICTS[reconciliation.verdict] || {}).label || reconciliation.verdict}
                  />
                </Box>

                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {reconciliation.headline}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {reconciliation.detail}
                </Typography>

                {/* Side by side, because the whole point is the difference between the two. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      We hold
                    </Typography>
                    <Typography variant="body2">{reconciliation.local?.status || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      payment {reconciliation.local?.lastPaymentId || 'none'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Square holds
                    </Typography>
                    <Typography variant="body2">
                      {reconciliation.square?.reachable ? (reconciliation.square.status || '—') : 'no answer'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {reconciliation.square?.chargedThroughDate
                        ? `billed through ${reconciliation.square.chargedThroughDate}`
                        : 'no charge recorded'}
                    </Typography>
                  </Box>
                </Box>

                {reconciliation.verdict === 'REPAIRABLE' && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Applying this will
                    </Typography>
                    <Box component="ul" sx={{ mt: 0.5, mb: 2, pl: 2.5 }}>
                      {(reconciliation.wouldChange || []).map((line) => (
                        <Typography component="li" variant="body2" color="text.secondary" key={line}>
                          {line}
                        </Typography>
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      color="warning"
                      disabled={applying}
                      startIcon={applying ? <CircularProgress size={16} /> : <SyncIcon />}
                      onClick={() => applyReconciliation(sub.subscriptionId)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      {applying ? 'Applying…' : 'Apply fix'}
                    </Button>
                  </>
                )}
              </Paper>
            )}

            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Book shipments ({detail.shipments?.length || 0})
              </Typography>
              {!detail.shipments?.length ? (
                <Typography variant="caption" color="text.secondary">No shipments raised.</Typography>
              ) : detail.shipments.map((s) => (
                <Box key={s.orderId} sx={{ py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={prettify(s.status)} color={bookColor(s.status)} variant="outlined" />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{s.orderNumber || `#${s.orderId}`}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(s.createdAt)}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {s.carrier ? `${s.carrier} · ` : ''}
                    {s.trackingUrl
                      ? <Link href={s.trackingUrl} target="_blank" rel="noopener noreferrer">{s.trackingNumber || 'Track'}</Link>
                      : (s.trackingNumber || 'No tracking yet')}
                  </Typography>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
            </Paper>

            {!!detail.deferredBooks?.length && (
              <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Deferred books</Typography>
                {detail.deferredBooks.map((d) => (
                  <Stack key={d.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }} flexWrap="wrap">
                    <Chip size="small" label={prettify(d.status)} color={d.status === 'PENDING' ? 'error' : 'default'} variant="outlined" />
                    <Typography variant="caption">Iteration {d.iterationNumber ?? '—'} · {d.sets} set(s)</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(d.deferredAt)}</Typography>
                  </Stack>
                ))}
              </Paper>
            )}

            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Unlocked content ({detail.unlockedIterations?.length || 0})
              </Typography>
              {!detail.unlockedIterations?.length ? (
                <Typography variant="caption" color="text.secondary">Nothing unlocked yet.</Typography>
              ) : detail.unlockedIterations.map((it) => (
                <Typography key={`${it.iterationId}-${it.bundleId}`} variant="caption" display="block">
                  #{it.iterationNumber ?? '—'} · {it.bundleTitle || `Bundle ${it.bundleId}`} · {formatDate(it.unlockedAt)}
                </Typography>
              ))}
            </Paper>

            {/* Replaces the raw audit list that stood here. Same events, plus the period each
                payment bought, the billing ledger and any support tickets — assembled server
                side rather than left as action-type chips to decode. */}
            <MemberTimelinePanel key={timelineKey} iboNumber={sub?.iboNumber} callApi={callApi} />
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default SubscribersPanel;
